/**
 * ConnectorDonutChart Molecule
 * Donut chart showing connector status breakdown
 */

import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip as RechartsTooltip } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/core/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/core/components/ui/tooltip';
import { cn } from '@/core/lib/utils';
import { ConnectorStatusBreakdown } from '../../types/dashboard-types';
import { formatNumber, formatPercentage } from '../../utils/currency-utils';

interface ConnectorDonutChartProps {
  data: ConnectorStatusBreakdown[];
  totalConnectors: number;
  isLoading?: boolean;
}

// Format status name for display (add spaces)
function formatStatusName(status: string): string {
  // Handle specific cases
  if (status === 'SuspendedEV') return 'Suspended EV';
  if (status === 'SuspendedEVSE') return 'Suspended EVSE';
  return status;
}

export function ConnectorDonutChart({
  data,
  totalConnectors,
  isLoading = false,
}: ConnectorDonutChartProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Connector Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-48 flex items-center justify-center">
            <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (data.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Connector Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-48 flex items-center justify-center text-gray-500">
            No connector data available
          </div>
        </CardContent>
      </Card>
    );
  }

  const chartData = data.map(item => ({
    name: item.status,
    displayName: formatStatusName(item.status),
    value: item.count,
    color: item.color,
    percentage: item.percentage,
  }));

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-xs md:text-sm font-medium text-black">Connector Status</CardTitle>
      </CardHeader>
      <CardContent className="p-2 md:p-4">
        <div className="flex items-start gap-5 md:gap-6">
          {/* Left side - Donut chart */}
          <div className="flex-shrink-0 w-20 h-20 md:w-24 md:h-24 relative">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={25}
                  outerRadius={38}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {chartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.color}
                    />
                  ))}
                </Pie>
                <RechartsTooltip
                  position={{ x: 0, y: 100 }}
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div className="bg-white p-2 border border-purple-300 rounded shadow-lg">
                          <p className="font-medium text-black text-xs md:text-sm">{data.displayName}</p>
                          <p className="text-xs md:text-sm text-black">
                            {formatNumber(data.value, 0)} ({formatPercentage(data.percentage)})
                          </p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            {/* Center total - positioned inside the donut */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-center">
                <div className="text-base md:text-lg font-bold text-black">{formatNumber(totalConnectors, 0)}</div>
                <div className="text-[8px] md:text-[9px] text-black">Total</div>
              </div>
            </div>
          </div>

          {/* Right side - Legend */}
          <TooltipProvider>
            <div className="flex-1 flex flex-col gap-0.5 md:gap-1 justify-center">
              {chartData.map((item, index) => (
                <Tooltip key={index} delayDuration={300}>
                  <TooltipTrigger asChild>
                    <div
                      className={cn(
                        'flex items-center gap-1 md:gap-1.5 rounded px-1 md:px-1.5 py-0.5'
                      )}
                    >
                      <div
                        className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: item.color }}
                      />
                      <span className="text-[10px] md:text-xs font-medium flex-shrink-0" style={{ color: '#000 !important', opacity: 1 }}>
                        {item.displayName}:
                      </span>
                      <span className="text-[10px] md:text-xs font-semibold ml-auto flex-shrink-0 tabular-nums" style={{ color: '#000 !important', opacity: 1 }}>
                        {formatNumber(item.value, 0)}
                      </span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="text-center">
                      <p className="font-medium text-black text-xs md:text-sm">{item.displayName}</p>
                      <p className="text-xs md:text-sm text-black">
                        {formatNumber(item.value, 0)} connectors ({formatPercentage(item.percentage)})
                      </p>
                    </div>
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>
          </TooltipProvider>
        </div>
      </CardContent>
    </Card>
  );
}
